import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Link } from 'react-router-dom';
import EdgeSection, { EdgePlayHeader, getEdgePlay, isSameTeam, isStrongConfidence, isStrongGrade } from './EdgeSection';
const GameCard = ({ game }) => {
    // Check if game is completed and has results
    const isGameFinal = game.result?.final || false;
    const hasUserBet = game.bet_outcome !== undefined;
    // Debug logging for Rams vs 49ers game
    if (game.home_team === 'Los Angeles Rams' || game.away_team === 'Los Angeles Rams') {
        console.log('🎯 GameCard rendering Rams game:', {
            id: game.id,
            teams: `${game.away_team} @ ${game.home_team}`,
            hasResult: !!game.result,
            result: game.result,
            isGameFinal,
            hasUserBet
        });
    }
    const getBetDisplayText = () => {
        // Show our prediction with the spread like MLB does
        const team = game.team_to_cover || game.home_team;
        const spread = game.spread_line;
        if (spread !== null && spread !== undefined) {
            const spreadText = spread > 0 ? `+${spread}` : `${spread}`;
            return `${team} ${spreadText}`;
        }
        return team;
    };
    const getSpreadOdds = () => {
        // Show the spread odds for our predicted team
        const homeSpreadOdds = game.home_spread_odds || -110;
        const awaySpreadOdds = game.away_spread_odds || -110;
        // Determine which team we predicted and show their spread odds
        const isHomePick = game.team_to_cover === game.home_team;
        const odds = isHomePick ? homeSpreadOdds : awaySpreadOdds;
        return odds > 0 ? `+${odds}` : `${odds}`;
    };
    const getConfidenceColor = () => {
        const level = game.confidence_level;
        switch (level) {
            case 'HIGH': return '#22c55e'; // Green for high confidence
            case 'MEDIUM_PLUS': return '#3b82f6'; // Blue for medium-plus
            case 'MEDIUM': return '#f59e0b'; // Yellow for medium  
            case 'LOW': return '#6b7280'; // Gray for low
            default: return '#6b7280'; // Default gray
        }
    };
    // Bet grading functions (same as MLB)
    const getBetGrade = () => {
        return game.betting_indicators?.bet_ranking?.grade || 'C';
    };
    const getBetGradeColor = (grade) => {
        if (!grade)
            return '#6c757d';
        const gradeUpper = grade.toUpperCase();
        if (gradeUpper.startsWith('A'))
            return '#198754'; // Green
        if (gradeUpper.startsWith('B'))
            return '#0dcaf0'; // Light blue
        if (gradeUpper.startsWith('C'))
            return '#ffc107'; // Yellow
        if (gradeUpper.startsWith('D'))
            return '#fd7e14'; // Orange
        if (gradeUpper.startsWith('F'))
            return '#dc3545'; // Red
        return '#6c757d';
    };
    const formatOdds = (odds) => {
        const value = odds || -110;
        return value > 0 ? `+${value}` : `${value}`;
    };
    const hasEdge = !!game.edge_context && game.edge_rank !== undefined;
    const play = getEdgePlay(game.edge_context);
    const showConfidence = isStrongConfidence(game.confidence_level) &&
        (!play || isSameTeam(play.team, game.team_to_cover));
    const showGrade = isStrongGrade(game.betting_indicators?.bet_ranking?.grade);
    // Get bet outcome indicator (green check or red X)
    const getBetOutcomeIndicator = () => {
        if (!hasUserBet || !isGameFinal)
            return null;
        const betWon = game.bet_outcome?.status === 'won';
        const icon = betWon ? '✓' : '✗';
        const color = betWon ? '#22c55e' : '#dc3545';
        const bgColor = betWon ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)';
        return (_jsx("div", { style: {
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: bgColor,
                border: `2px solid ${color}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                fontWeight: 'bold',
                color: color,
                zIndex: 10
            }, title: betWon ? 'Bet Won!' : 'Bet Lost', children: icon }));
    };
    return (_jsxs("div", { className: "card h-100", style: {
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            position: 'relative'
        }, onMouseEnter: (e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }, onMouseLeave: (e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
        }, children: [getBetOutcomeIndicator(), _jsxs("div", { className: "card-body", style: { padding: '16px' }, children: [!isGameFinal && (_jsx("div", { className: "d-flex justify-content-between align-items-center mb-3", children: _jsx("div", { className: "team-info", children: _jsxs("h5", { className: "mb-0", style: { fontSize: '18px', fontWeight: '600', color: 'var(--bs-body-color)' }, children: [game.away_team, " @ ", game.home_team] }) }) })), isGameFinal && game.result && (() => {
                        // For spread betting, check if our predicted team covered the spread
                        // Use spread_prediction_correct from the enriched game data
                        const gameData = game;
                        let wasCorrect = false;
                        // Priority 1: Check spread_coverage.spread_prediction_correct (most accurate)
                        if (gameData.spread_coverage?.spread_prediction_correct !== undefined) {
                            wasCorrect = gameData.spread_coverage.spread_prediction_correct;
                        }
                        // Priority 2: Check prediction_correct field
                        else if (gameData.prediction_correct !== undefined) {
                            wasCorrect = gameData.prediction_correct;
                        }
                        // Priority 3: Fallback to moneyline winner check
                        else {
                            const winner = game.result.winner;
                            const predictedTeam = game.team_to_cover;
                            const homeTeam = game.home_team;
                            const predictedWinner = predictedTeam === homeTeam ? 'home' : 'away';
                            wasCorrect = predictedWinner === winner;
                        }
                        const borderColor = wasCorrect ? '#22c55e' : '#dc3545';
                        const bgColor = wasCorrect ? 'rgba(34, 197, 94, 0.1)' : 'rgba(220, 53, 69, 0.1)';
                        const labelColor = wasCorrect ? '#22c55e' : '#dc3545';
                        const label = wasCorrect ? 'FINAL - CORRECT' : 'FINAL - INCORRECT';
                        return (_jsxs("div", { className: "mb-3", style: {
                                backgroundColor: bgColor,
                                border: `1px solid ${borderColor}`,
                                borderRadius: '6px',
                                padding: '12px',
                                textAlign: 'center'
                            }, children: [_jsx("div", { style: { fontSize: '11px', color: labelColor, fontWeight: '600', marginBottom: '4px' }, children: label }), _jsxs("div", { style: { fontSize: '20px', fontWeight: '700', color: 'var(--bs-body-color)' }, children: [game.result.away_score, " - ", game.result.home_score] }), _jsxs("div", { style: { fontSize: '12px', color: '#6b7280', marginTop: '4px' }, children: ["Winner: ", game.result.winner === 'home' ? game.home_team : game.away_team] })] }));
                    })(), hasEdge ? (_jsx(EdgePlayHeader, { rank: game.edge_rank, play: play, playOdds: play ? formatOdds(play.side === 'home' ? game.home_spread_odds : game.away_spread_odds) : undefined, leanText: getBetDisplayText(), leanOdds: getSpreadOdds() })) : (_jsx("div", { className: "main-prediction mb-3 text-center", children: _jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("div", { className: "prediction-text", style: { fontSize: '18px', fontWeight: '700', color: 'var(--bs-body-color)' }, children: getBetDisplayText() }), _jsx("div", { className: "odds-text", style: { fontSize: '18px', fontWeight: '700', color: '#22c55e' }, children: getSpreadOdds() })] }) })), showConfidence && (_jsxs("div", { className: "confidence-section mb-3", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsx("span", { className: "text-muted", style: { fontSize: '12px' }, children: "Pick Confidence" }), _jsxs("span", { style: { fontSize: '12px', fontWeight: '600', color: 'var(--bs-body-color)' }, children: [game.display_confidence.toFixed(0), "% ", getBetDisplayText()] })] }), _jsx("div", { className: "progress", style: { height: '10px', borderRadius: '5px', backgroundColor: '#374151' }, children: _jsx("div", { className: "progress-bar", style: {
                                        width: `${game.display_confidence}%`,
                                        backgroundColor: getConfidenceColor(),
                                        borderRadius: '5px'
                                    } }) })] })), (showConfidence || showGrade) && (_jsx("div", { className: "badges-section mb-3", children: _jsxs("div", { className: "d-flex justify-content-between", children: [showConfidence && (_jsxs("div", { className: "text-center", style: { flex: 1 }, children: [_jsx("div", { style: { fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }, children: "Confidence" }), _jsx("div", { style: {
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                color: getConfidenceColor()
                                            }, children: game.confidence_level })] })), showGrade && (_jsxs("div", { className: "text-center", style: { flex: 1 }, children: [_jsx("div", { style: { fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }, children: "Bet Grade" }), _jsx("div", { style: {
                                                fontSize: '12px',
                                                fontWeight: '700',
                                                color: getBetGradeColor(getBetGrade())
                                            }, children: getBetGrade() })] }))] }) })), _jsx(EdgeSection, { rank: game.edge_rank, context: game.edge_context, homeTeam: game.home_team, awayTeam: game.away_team }), _jsxs(Link, { to: `/dashboard/game/${game.id}`, className: "btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center", style: {
                            fontSize: '12px',
                            fontWeight: '500',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            textDecoration: 'none',
                            minHeight: '44px'
                        }, children: [_jsx("i", { className: "fas fa-chart-line me-2" }), "View Analysis"] })] })] }));
};
export default GameCard;
